/**
 * Символы раздела «Декор» для плана — по компоненту на элемент каталога (кроме ковров:
 * у них своя форма RugShape без подложки).
 *
 * Рисунок задан в собственном viewBox символа — в миллиметрах типового габарита —
 * и растягивается в габарит объекта (UI.SymbolFrame). Цвет — через currentColor.
 * Картина и экран висят на стене: сверху это тонкая полоса вдоль стены, спереди
 * (на развёртке) — рама с рисунком. Перед предмета — к нижнему краю символа.
 * Ключ реестра — id элемента каталога.
 */
import { DecorId } from "@/shared/catalog";
import { UIKit, type SymbolComponent } from "@/shared/ui";

const line = { fill: "none", stroke: "currentColor" } as const;

/** Картина: сверху — рама вдоль стены, спереди — рама и пейзаж с горами */
export const PaintingSymbol: SymbolComponent = ({ facing = "plan", ...props }) =>
	facing === "front" ? (
		<UIKit.SymbolFrame viewBox="0 0 800 600" {...props}>
			<rect x="10" y="10" width="780" height="580" {...line} strokeWidth="20" />
			<rect x="50" y="50" width="700" height="500" {...line} strokeWidth="6" />
			<path
				d="M50 440l170-190 120 120 150-200 260 270"
				{...line}
				strokeWidth="10"
				strokeLinejoin="round"
			/>
			<circle cx="590" cy="160" r="40" {...line} strokeWidth="8" />
		</UIKit.SymbolFrame>
	) : (
		<UIKit.SymbolFrame viewBox="0 0 800 30" {...props}>
			<rect x="0" y="0" width="800" height="30" fill="currentColor" />
		</UIKit.SymbolFrame>
	);

/** Широкая картина: спереди — абстракция из цветовых полей и круга */
export const WidePaintingSymbol: SymbolComponent = ({ facing = "plan", ...props }) =>
	facing === "front" ? (
		<UIKit.SymbolFrame viewBox="0 0 1200 500" {...props}>
			<rect x="8" y="8" width="1184" height="484" {...line} strokeWidth="16" />
			<rect x="40" y="270" width="480" height="190" {...line} strokeWidth="8" />
			<rect x="760" y="40" width="400" height="300" {...line} strokeWidth="8" />
			<circle cx="560" cy="200" r="110" {...line} strokeWidth="8" />
		</UIKit.SymbolFrame>
	) : (
		<UIKit.SymbolFrame viewBox="0 0 1200 25" {...props}>
			<rect x="0" y="0" width="1200" height="25" fill="currentColor" />
		</UIKit.SymbolFrame>
	);

/** Экран для проектора: сверху — корпус вдоль стены, спереди — корпус, кайма и полотно */
export const ProjectorScreenSymbol: SymbolComponent = ({ facing = "plan", ...props }) =>
	facing === "front" ? (
		<UIKit.SymbolFrame viewBox="0 0 2000 1250" {...props}>
			<rect x="0" y="0" width="2000" height="80" rx="40" fill="currentColor" />
			<rect x="30" y="90" width="1940" height="1140" {...line} strokeWidth="14" />
			<rect x="80" y="140" width="1840" height="920" {...line} strokeWidth="8" />
			<path d="M30 1230h1940" stroke="currentColor" strokeWidth="30" />
		</UIKit.SymbolFrame>
	) : (
		<UIKit.SymbolFrame viewBox="0 0 2000 80" {...props}>
			<rect x="6" y="6" width="1988" height="68" rx="34" {...line} strokeWidth="12" />
			<path d="M40 40h1920" stroke="currentColor" strokeWidth="8" />
		</UIKit.SymbolFrame>
	);

/** Цветок сверху: круг горшка и листья веером */
export const PlantSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 100 100" {...props}>
		<circle cx="50" cy="50" r="30" {...line} strokeWidth="3" />
		{[0, 51, 103, 154, 206, 257, 309].map((angle) => (
			<ellipse
				key={angle}
				cx="50"
				cy="26"
				rx="9"
				ry="23"
				{...line}
				strokeWidth="2.5"
				transform={`rotate(${angle} 50 50)`}
			/>
		))}
		<circle cx="50" cy="50" r="4" fill="currentColor" />
	</UIKit.SymbolFrame>
);

/** Миска сверху: край и корм */
export const PetBowlSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 100 100" {...props}>
		<circle cx="50" cy="50" r="46" {...line} strokeWidth="6" />
		<circle cx="50" cy="50" r="32" fill="currentColor" opacity="0.35" />
	</UIKit.SymbolFrame>
);

/** Поилка-фонтан сверху: корпус, чаша и струйка в центре */
export const PetFountainSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 100 100" {...props}>
		<circle cx="50" cy="50" r="46" {...line} strokeWidth="5" />
		<circle cx="50" cy="50" r="36" {...line} strokeWidth="3" strokeDasharray="6 4" />
		<circle cx="50" cy="50" r="8" fill="currentColor" />
	</UIKit.SymbolFrame>
);

/** Лоток сверху: борт и наполнитель */
export const LitterBoxSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 400 550" {...props}>
		<rect x="8" y="8" width="384" height="534" rx="40" {...line} strokeWidth="16" />
		<rect x="40" y="40" width="320" height="470" rx="20" {...line} strokeWidth="6" />
		{[120, 200, 280].flatMap((x) =>
			[140, 270, 400].map((y) => (
				<circle key={`${x}${y}`} cx={x} cy={y} r="10" fill="currentColor" />
			)),
		)}
	</UIKit.SymbolFrame>
);

/** Стопка книг сверху: две книги, верхняя чуть повёрнута */
export const BookStackSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 300 220" {...props}>
		<rect x="10" y="10" width="280" height="200" {...line} strokeWidth="8" />
		<rect
			x="40"
			y="30"
			width="220"
			height="160"
			{...line}
			strokeWidth="8"
			transform="rotate(-6 150 110)"
		/>
		<path d="M60 40v140" stroke="currentColor" strokeWidth="6" transform="rotate(-6 150 110)" />
	</UIKit.SymbolFrame>
);

/** Книги в ряд сверху: корешки между двумя держателями */
export const BookRowSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 450 180" {...props}>
		<path d="M6 10v160M444 10v160" stroke="currentColor" strokeWidth="12" />
		{Array.from({ length: 14 }, (_, i) => (
			<rect
				key={i}
				x={16 + i * 30}
				y={i % 3 === 0 ? 14 : 24}
				width="26"
				height={i % 2 ? 140 : 150}
				{...line}
				strokeWidth="4"
			/>
		))}
	</UIKit.SymbolFrame>
);

/** Настольная лампа сверху: основание у стены, ножка и плафон впереди */
export const DeskLampSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 160 300" {...props}>
		<circle cx="80" cy="80" r="72" {...line} strokeWidth="6" />
		<path d="M80 80v140" stroke="currentColor" strokeWidth="8" strokeLinecap="round" />
		<circle cx="80" cy="220" r="70" {...line} strokeWidth="8" />
		<circle cx="80" cy="220" r="14" fill="currentColor" />
	</UIKit.SymbolFrame>
);

/**
 * Гитара в осях длиной 410: корпус-«восьмёрка» (нижняя дека шире верхней, талия между
 * ними), гриф с ладами и головка с колками; струны идут от подставки до головки.
 * Акустика — с розеткой, электрогитара — с двумя датчиками и ручками вместо неё.
 */
const GuitarBody = ({ electric = false }: { electric?: boolean }) => (
	<>
		<path
			d="M10 0C10-46 40-70 80-68C114-66 124-42 146-42C166-48 192-56 214-40C236-26 236 26 214 40C192 56 166 48 146 42C124 42 114 66 80 68C40 70 10 46 10 0Z"
			{...line}
			strokeWidth="7"
			strokeLinejoin="round"
		/>
		<rect x="226" y="-9" width="146" height="18" rx="3" {...line} strokeWidth="6" />
		<path
			d={Array.from({ length: 7 }, (_, i) => `M${246 + i * 18} -9V9`).join("")}
			stroke="currentColor"
			strokeWidth="2.5"
		/>
		<path d="M372-11L410-16V16L372 11Z" {...line} strokeWidth="6" strokeLinejoin="round" />
		{[382, 396].flatMap((x) =>
			[-20, 20].map((y) => <circle key={`${x}${y}`} cx={x} cy={y} r="4" fill="currentColor" />),
		)}
		{electric ? (
			<>
				<rect x="150" y="-16" width="12" height="32" rx="3" fill="currentColor" opacity="0.7" />
				<rect x="110" y="-16" width="12" height="32" rx="3" fill="currentColor" opacity="0.7" />
				<circle cx="62" cy="36" r="7" {...line} strokeWidth="4" />
				<circle cx="42" cy="22" r="7" {...line} strokeWidth="4" />
			</>
		) : (
			<circle cx="172" cy="0" r="20" {...line} strokeWidth="6" />
		)}
		<rect x="68" y="-22" width="12" height="44" rx="3" fill="currentColor" />
		<path d="M80-5H372M80 0H372M80 5H372" stroke="currentColor" strokeWidth="1.2" opacity="0.6" />
	</>
);

/** Акустическая гитара на стойке сверху: силуэт гитары по диагонали габарита */
export const GuitarSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 420 280" {...props}>
		<g transform="translate(210 140) rotate(-22) scale(0.95) translate(-210 0)">
			<GuitarBody />
		</g>
	</UIKit.SymbolFrame>
);

/** Электрогитара сверху по диагонали, справа — комбоусилитель с динамиком */
export const ElectricGuitarSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 725 430" {...props}>
		<g transform="translate(250 215) rotate(-28) scale(1.05) translate(-210 0)">
			<GuitarBody electric />
		</g>
		<rect x="505" y="170" width="210" height="250" rx="16" {...line} strokeWidth="10" />
		<circle cx="610" cy="300" r="70" {...line} strokeWidth="8" />
		<path d="M520 200H700" stroke="currentColor" strokeWidth="6" opacity="0.6" />
	</UIKit.SymbolFrame>
);

/** Домик для кошки сверху: площадки и столбики из сизаля кружками */
export const CatTreeSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1000 600" {...props}>
		<rect x="10" y="10" width="980" height="580" rx="30" {...line} strokeWidth="14" />
		<rect x="60" y="80" width="420" height="440" rx="40" {...line} strokeWidth="10" />
		{[
			[640, 180],
			[860, 180],
			[750, 440],
		].map(([cx, cy]) => (
			<circle key={`${cx}${cy}`} cx={cx} cy={cy} r="60" fill="currentColor" opacity="0.5" />
		))}
	</UIKit.SymbolFrame>
);

/** Лежанка для собаки сверху: мягкий борт и подушка */
export const DogBedSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 100 100" {...props}>
		<circle cx="50" cy="50" r="46" {...line} strokeWidth="5" />
		<circle cx="50" cy="52" r="30" {...line} strokeWidth="3" strokeDasharray="6 4" />
	</UIKit.SymbolFrame>
);

/** Ударная установка сверху: бочка в центре, барабаны и тарелки вокруг, стул спереди */
export const DrumKitSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1700 830" {...props}>
		<rect x="650" y="40" width="400" height="320" rx="40" {...line} strokeWidth="12" />
		{[
			[480, 300, 150],
			[1220, 300, 150],
			[640, 520, 140],
			[1060, 520, 160],
		].map(([cx, cy, r]) => (
			<circle key={`${cx}${cy}`} cx={cx} cy={cy} r={r} {...line} strokeWidth="12" />
		))}
		{[
			[200, 200],
			[1500, 200],
		].map(([cx, cy]) => (
			<circle
				key={`${cx}${cy}`}
				cx={cx}
				cy={cy}
				r="180"
				{...line}
				strokeWidth="6"
				strokeDasharray="24 14"
			/>
		))}
		<circle cx="850" cy="720" r="100" fill="currentColor" opacity="0.5" />
	</UIKit.SymbolFrame>
);

/** Мольберт сверху: тренога и холст поперёк */
export const EaselSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 765 730" {...props}>
		<path
			d="M382 300L60 700M382 300L705 700M382 300V20"
			stroke="currentColor"
			strokeWidth="18"
			strokeLinecap="round"
		/>
		<path d="M80 300H685" stroke="currentColor" strokeWidth="40" strokeLinecap="round" />
	</UIKit.SymbolFrame>
);

/** Напольная ваза сверху: горлышко и веер сухоцвета */
export const VaseSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 100 100" {...props}>
		<circle cx="50" cy="50" r="22" {...line} strokeWidth="4" />
		<circle cx="50" cy="50" r="8" fill="currentColor" />
		{[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => (
			<path
				key={angle}
				d="M50 42V6"
				stroke="currentColor"
				strokeWidth="2"
				strokeDasharray="4 3"
				transform={`rotate(${angle} 50 50)`}
			/>
		))}
	</UIKit.SymbolFrame>
);

/** Напольное зеркало сверху: тонкая рама, опора отходит назад к стене */
export const MirrorSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 560 290" {...props}>
		<rect x="8" y="200" width="544" height="60" rx="10" {...line} strokeWidth="12" />
		<path d="M280 200V30" stroke="currentColor" strokeWidth="10" strokeDasharray="20 12" />
	</UIKit.SymbolFrame>
);

/** Камин сверху: портал П-образно и топка в глубине */
export const FireplaceSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1200 400" {...props}>
		<rect x="8" y="8" width="1184" height="384" rx="10" {...line} strokeWidth="14" />
		<rect x="300" y="60" width="600" height="240" fill="currentColor" opacity="0.5" />
		<path d="M8 330H1192" stroke="currentColor" strokeWidth="8" />
	</UIKit.SymbolFrame>
);

/** Корзина для белья сверху: плетёный край и крышка */
export const BasketSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 100 100" {...props}>
		<circle cx="50" cy="50" r="46" {...line} strokeWidth="5" />
		<circle cx="50" cy="50" r="38" {...line} strokeWidth="2" />
		<path d="M20 50h60M50 20v60" stroke="currentColor" strokeWidth="2" opacity="0.5" />
	</UIKit.SymbolFrame>
);

/** Аквариум сверху: тумба, стекло и полоска грунта с растениями */
export const AquariumSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1060 450" {...props}>
		<rect x="8" y="8" width="1044" height="434" rx="10" {...line} strokeWidth="14" />
		<rect x="40" y="40" width="980" height="370" fill="currentColor" opacity="0.15" />
		<path d="M80 225C230 160 380 290 530 225S830 160 980 225" {...line} strokeWidth="8" />
	</UIKit.SymbolFrame>
);

/** Декоративная подушка сверху: мягкий квадрат с просевшими краями и шов */
export const CushionSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 100 100" {...props}>
		<path
			d="M8 8Q50 18 92 8Q82 50 92 92Q50 82 8 92Q18 50 8 8Z"
			{...line}
			strokeWidth="4"
			strokeLinejoin="round"
		/>
		<path d="M22 22Q50 30 78 22Q70 50 78 78Q50 70 22 78Q30 50 22 22Z" {...line} strokeWidth="2" />
	</UIKit.SymbolFrame>
);

/** Настольная лампа с абажуром сверху: круг абажура, верхний обод и лампа в центре */
export const TableLampSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 100 100" {...props}>
		<circle cx="50" cy="50" r="46" {...line} strokeWidth="5" />
		<circle cx="50" cy="50" r="30" {...line} strokeWidth="3" />
		<path d="M30 50h40M50 30v40" stroke="currentColor" strokeWidth="2" opacity="0.6" />
		<circle cx="50" cy="50" r="7" fill="currentColor" />
	</UIKit.SymbolFrame>
);

/** Дизайнерская консоль сверху: столешница со скруглёнными краями и плавная опора под ней */
export const OrganicConsoleSymbol: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1200 445" {...props}>
		<rect x="8" y="8" width="1184" height="429" rx="120" {...line} strokeWidth="14" />
		<path
			d="M180 222C330 90 470 350 600 222S870 90 1020 222"
			{...line}
			strokeWidth="10"
			strokeDasharray="30 18"
		/>
	</UIKit.SymbolFrame>
);

/** Настенные часы: сверху — полоса вдоль стены, спереди — рама, деления и стрелки */
export const ClockSymbol: SymbolComponent = ({ facing = "plan", ...props }) =>
	facing === "front" ? (
		<UIKit.SymbolFrame viewBox="0 0 400 400" {...props}>
			<circle cx="200" cy="200" r="190" {...line} strokeWidth="16" />
			<circle cx="200" cy="200" r="160" {...line} strokeWidth="4" />
			{[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((angle) => (
				<path
					key={angle}
					d={angle % 90 === 0 ? "M200 50V90" : "M200 50V72"}
					stroke="currentColor"
					strokeWidth="8"
					transform={`rotate(${angle} 200 200)`}
				/>
			))}
			<path
				d="M200 200L200 105M200 200L270 240"
				stroke="currentColor"
				strokeWidth="10"
				strokeLinecap="round"
			/>
			<circle cx="200" cy="200" r="10" fill="currentColor" />
		</UIKit.SymbolFrame>
	) : (
		<UIKit.SymbolFrame viewBox="0 0 400 125" {...props}>
			<rect x="6" y="6" width="388" height="113" rx="50" {...line} strokeWidth="12" />
		</UIKit.SymbolFrame>
	);

export const DECOR_SYMBOLS: Partial<Record<DecorId, SymbolComponent>> = {
	[DecorId.Painting]: PaintingSymbol,
	[DecorId.PaintingWide]: WidePaintingSymbol,
	[DecorId.ProjectorScreen]: ProjectorScreenSymbol,
	[DecorId.PlantFloor]: PlantSymbol,
	[DecorId.PlantTable]: PlantSymbol,
	[DecorId.PlantMonstera]: PlantSymbol,
	[DecorId.Guitar]: GuitarSymbol,
	[DecorId.GuitarElectric]: ElectricGuitarSymbol,
	[DecorId.PlantFicus]: PlantSymbol,
	[DecorId.PlantOlive]: PlantSymbol,
	[DecorId.PlantSnake]: PlantSymbol,
	[DecorId.CatTree]: CatTreeSymbol,
	[DecorId.DogBed]: DogBedSymbol,
	[DecorId.DrumKit]: DrumKitSymbol,
	[DecorId.Easel]: EaselSymbol,
	[DecorId.VaseFloor]: VaseSymbol,
	[DecorId.MirrorFloor]: MirrorSymbol,
	[DecorId.Fireplace]: FireplaceSymbol,
	[DecorId.LaundryBasket]: BasketSymbol,
	[DecorId.PlantPalm]: PlantSymbol,
	[DecorId.Aquarium]: AquariumSymbol,
	[DecorId.CatBowl]: PetBowlSymbol,
	[DecorId.CatFountain]: PetFountainSymbol,
	[DecorId.CatLitter]: LitterBoxSymbol,
	[DecorId.BooksStack]: BookStackSymbol,
	[DecorId.BooksRow]: BookRowSymbol,
	[DecorId.DeskLamp]: DeskLampSymbol,
	[DecorId.CushionBaroque]: CushionSymbol,
	[DecorId.CushionPlaid]: CushionSymbol,
	[DecorId.CushionRed]: CushionSymbol,
	[DecorId.CushionRhombus]: CushionSymbol,
	[DecorId.CushionWoven]: CushionSymbol,
	[DecorId.TableLamp]: TableLampSymbol,
	[DecorId.DeskLampModern]: TableLampSymbol,
	[DecorId.LampWood]: TableLampSymbol,
	[DecorId.PetBedDonut]: DogBedSymbol,
	[DecorId.LaundryBasket2]: BasketSymbol,
	[DecorId.TableOrganic]: OrganicConsoleSymbol,
	[DecorId.ClockWall]: ClockSymbol,
};
